import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Divider,
  Stack,
  Grid,
  Drawer,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  DialogTitle,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import MoreIcon from "@mui/icons-material/More";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ArchiveIcon from "@mui/icons-material/Archive";
import StorageIcon from "@mui/icons-material/Storage";

import {
  MyIssuesTable,
  OpenIssuesTable,
  MutedIssuesTable,
  ResolvedIssuesTable,
  AllIssuesTable,
} from "../IssuesTable";

import {
  usePrefetchAllIssuesTableQueries,
  usePrefetchOpenIssuesTableQueries,
  usePrefetchResolvedIssuesTableQueries,
  usePrefetchMutedAllIssuesTableQueries,
} from "../IssuesTable/AllIssuesTable";

import { getAllOpenIssueCount, getMyOpenIssueCount } from "./queries";
import { Monitor } from "../fixtures";
import {
  CreateMonitorInput,
  EditMonitorInput,
  createMonitor,
  deleteMonitor,
  editMonitor,
} from "./mutations";

import NavButton from "./NavButton";
import NavBadge from "./NavBadge";
import MonitorList from "./MonitorList";
import MonitorCreate from "./MonitorCreate";
import MonitorEdit from "./MonitorEdit";
import IssueDetail from "./IssueDetail";

type IssueView = "mine" | "open" | "muted" | "resolved" | "everything";
type MonitorView = "closed" | "list" | "create" | "edit";
type DetailView = "closed" | "open";

export default function Monitoring() {
  const queryClient = useQueryClient();

  usePrefetchAllIssuesTableQueries();
  usePrefetchMutedAllIssuesTableQueries();
  usePrefetchOpenIssuesTableQueries();
  usePrefetchResolvedIssuesTableQueries();

  const [issueView, setIssueView] = useState<IssueView>("mine");

  const [monitorView, setMonitorView] = useState<MonitorView>("closed");
  const [monitorEditId, setMonitorEditId] = useState("");

  const [detailView, setDetailView] = useState<DetailView>("closed");
  const [detailViewId, setDetailViewId] = useState<string | null>(null);

  const myOpenIssueCountQuery = useQuery({
    queryKey: ["my", "issues", "openIssueCount"],
    queryFn: getMyOpenIssueCount,
  });

  const allOpenIssueCountQuery = useQuery({
    queryKey: ["all", "issues", "openIssueCount"],
    queryFn: getAllOpenIssueCount,
  });

  // since you can't apparently store closures in state
  // and have it typecheck
  const postCreateView = useRef<"list" | "closed">("closed");

  const onPostCreate = useCallback(() => {
    if (postCreateView.current) {
      setMonitorView(postCreateView.current);
    }
  }, []);

  const onShowMonitors = () => {
    postCreateView.current = "list";
    setMonitorView("list");
  };

  const onCreateMonitor = () => {
    postCreateView.current = "closed";
    setMonitorView("create");
  };

  const deleteMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await deleteMonitor(id);
    },
    mutationKey: ["monitors", "delete"],
    onMutate: async (variables) => {
      const listQueryKey = ["allMonitors"];
      if (!listQueryKey) return;

      await queryClient.cancelQueries({ queryKey: listQueryKey });
      const previous = queryClient.getQueryData(listQueryKey);

      queryClient.setQueryData(listQueryKey, (old: any) => {
        const updated = {
          ...old,
          monitors: old.monitors.filter(
            (monitor: Monitor) => monitor.id !== variables.id,
          ),
        };

        return updated;
      });

      return { previous };
    },
    onSuccess: (data, variables) => {
      const invalidateQueryKey = ["allMonitors"];

      if (invalidateQueryKey) {
        queryClient.invalidateQueries({
          queryKey: invalidateQueryKey,
        });
      }
    },
    onError: (err, variables, context) => {
      const listQueryKey = ["allMonitors"];

      if (context?.previous) {
        if (listQueryKey) {
          queryClient.setQueryData(listQueryKey, context.previous);
        }
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: async (variables: CreateMonitorInput) => {
      await createMonitor(variables);
    },
    mutationKey: ["monitors", "create"],
    onMutate: async (variables) => {
      // TODO: optimistic update

      return { previous: undefined };
    },
    onSuccess: (data, variables) => {
      const invalidateQueryKey = ["allMonitors"];

      if (invalidateQueryKey) {
        queryClient.invalidateQueries({
          queryKey: invalidateQueryKey,
        });
      }
    },
    onError: (err, variables, context) => {
      const listQueryKey = ["allMonitors"];
      if (context?.previous) {
        if (listQueryKey) {
          queryClient.setQueryData(listQueryKey, context.previous);
        }
      }
    },
  });

  const editMutation = useMutation({
    mutationFn: async (variables: { id: string } & EditMonitorInput) => {
      await editMonitor(variables.id, variables);
    },
    mutationKey: ["monitors", "edit"],
    onMutate: async (variables) => {
      // TODO: optimistic update

      return { previous: undefined };
    },
    onSuccess: (data, variables) => {
      const invalidateQueryKey = ["allMonitors"];

      if (invalidateQueryKey) {
        queryClient.invalidateQueries({
          queryKey: invalidateQueryKey,
        });
      }

      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: ["getMonitor", variables.id],
        });
      }
    },
    onError: (err, variables, context) => {
      const listQueryKey = ["allMonitors"];
      if (context?.previous) {
        if (listQueryKey) {
          queryClient.setQueryData(listQueryKey, context.previous);
        }
      }
    },
  });

  const onSelectIssue = useCallback((issueId: string) => {
    setDetailViewId(issueId);
    setDetailView("open");
  }, []);

  return (
    <>
      <Grid container>
        <Grid item xs={2} />
        <Grid item xs={10}>
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginTop={1}
              marginBottom={3}
            >
              <Box>
                <Typography variant="h4" component="h1" fontWeight={700}>
                  Monitoring Issues
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={2}>
          <Stack sx={{ alignItems: "flex-start", paddingRight: "1.5em" }}>
            <div
              style={{
                minHeight: "72px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NavButton
                startIcon={<PersonIcon />}
                selected={issueView === "mine"}
                onClick={() => setIssueView("mine")}
              >
                <NavBadge
                  badgeContent={myOpenIssueCountQuery.data?.open}
                  selected={issueView === "mine"}
                >
                  My Issues
                </NavBadge>
              </NavButton>
            </div>
          </Stack>
          <Stack
            sx={{
              alignItems: "flex-start",
              paddingRight: "1.5em",
              gap: "0.5em",
            }}
          >
            <Divider flexItem />
            <NavButton
              startIcon={<MoreIcon />}
              selected={issueView === "open"}
              onClick={() => setIssueView("open")}
            >
              <NavBadge
                badgeContent={allOpenIssueCountQuery.data?.open}
                selected={issueView === "open"}
              >
                Open
              </NavBadge>
            </NavButton>
            <NavButton
              startIcon={<RemoveCircleIcon />}
              selected={issueView === "muted"}
              onClick={() => setIssueView("muted")}
            >
              Muted
            </NavButton>
            <NavButton
              startIcon={<ArchiveIcon />}
              selected={issueView === "resolved"}
              onClick={() => setIssueView("resolved")}
            >
              Resolved
            </NavButton>
            <NavButton
              startIcon={<StorageIcon />}
              selected={issueView === "everything"}
              onClick={() => setIssueView("everything")}
            >
              Everything
            </NavButton>
          </Stack>
        </Grid>
        <Grid item xs={10}>
          {issueView === "mine" ? (
            <MyIssuesTable
              key="mine"
              description="my issues"
              onCreateMonitor={onCreateMonitor}
              onShowMonitors={onShowMonitors}
              onSelectIssue={onSelectIssue}
            />
          ) : issueView === "open" ? (
            <OpenIssuesTable
              key="open"
              onCreateMonitor={onCreateMonitor}
              onShowMonitors={onShowMonitors}
              onSelectIssue={onSelectIssue}
            />
          ) : issueView === "muted" ? (
            <MutedIssuesTable
              key="muted"
              onCreateMonitor={onCreateMonitor}
              onShowMonitors={onShowMonitors}
              onSelectIssue={onSelectIssue}
            />
          ) : issueView === "resolved" ? (
            <ResolvedIssuesTable
              key="resolved"
              onCreateMonitor={onCreateMonitor}
              onShowMonitors={onShowMonitors}
              onSelectIssue={onSelectIssue}
            />
          ) : (
            <AllIssuesTable
              key="all"
              onCreateMonitor={onCreateMonitor}
              onShowMonitors={onShowMonitors}
              onSelectIssue={onSelectIssue}
            />
          )}
        </Grid>
      </Grid>
      <Drawer
        anchor="right"
        open={monitorView !== "closed"}
        ModalProps={{ keepMounted: false }}
      >
        {monitorView === "list" && (
          <MonitorList
            onClose={() => {
              setMonitorView("closed");
            }}
            onCreateMonitor={() => {
              postCreateView.current = "list";
              setMonitorView("create");
            }}
            onDeleteMonitor={async (monitorId: string) => {
              await deleteMutation.mutateAsync({ id: monitorId });
            }}
            onEditMonitor={(monitorId) => {
              setMonitorView("edit");
              setMonitorEditId(monitorId);
            }}
          />
        )}
        {monitorView === "create" && (
          <MonitorCreate
            onCancel={onPostCreate}
            onDone={onPostCreate}
            onCreateMonitor={async (formData) => {
              await createMutation.mutateAsync(formData);
            }}
          />
        )}
        {monitorView === "edit" && (
          <MonitorEdit
            onCancel={() => {
              setMonitorEditId("");
              setMonitorView("list");
            }}
            onDone={() => {
              setMonitorEditId("");
              setMonitorView("list");
            }}
            monitorId={monitorEditId}
            onEditMonitor={async (id, formData) => {
              await editMutation.mutateAsync({ id, ...formData });
            }}
          />
        )}
      </Drawer>
      <Dialog
        open={detailView === "open"}
        onClose={() => {
          setDetailView("closed");
          setDetailViewId(null);
        }}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingBottom: 0,
          }}
        >
          <span>Monitor Information</span>
          <IconButton
            onClick={() => {
              setDetailView("closed");
              setDetailViewId(null);
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <IssueDetail issueId={detailViewId!} />
        </DialogContent>
      </Dialog>
    </>
  );
}
